import { useState, useEffect } from 'react';
import ModuleContainer from '../layout/ModuleContainer.jsx';
import InfoButton from '../common/InfoButton.jsx';

const FIB_LABELS = {
  '1':     { label: '1.0 — Swing High', color: 'text-korinth-danger' },
  '0.79':  { label: '0.79 — Golden Zone Top', color: 'text-korinth-accent' },
  '0.705': { label: '0.705 — OTE Sweet Spot', color: 'text-yellow-400' },
  '0.62':  { label: '0.62 — Golden Zone Bottom', color: 'text-korinth-accent' },
  '0.5':   { label: '0.5 — Equilibrium', color: 'text-korinth-muted' },
  '0':     { label: '0.0 — Swing Low', color: 'text-blue-400' },
};

export default function OTECalculator({ instrument }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    async function fetchSession() {
      try {
        const res = await fetch(`/api/futures-signals/session?instrument=${instrument}`);
        if (res.ok) setData(await res.json());
      } catch { /* silent */ }
    }
    fetchSession();
    const interval = setInterval(fetchSession, 30000);
    return () => clearInterval(interval);
  }, [instrument]);

  const ote = data?.oteLevels;
  const fvgs = data?.fvgs || [];

  return (
    <ModuleContainer title={<span>OTE Fibonacci Levels <InfoButton title="Optimal Trade Entry (OTE)">
        <p><b>OTE</b> — Optimal Trade Entry. An ICT concept where price retraces into a fibonacci zone from the prior session's swing before continuing.</p>
        <p><b>Golden Zone (0.62-0.79)</b> — The sweet spot for entries. Price retracing 62-79% of London's range offers the best risk/reward. This is where institutional orders cluster.</p>
        <p><b>0.705 (OTE Sweet Spot)</b> — The exact midpoint of the golden zone. ICT's preferred entry level.</p>
        <p><b>Equilibrium (0.5)</b> — The 50% retracement. Often acts as support/resistance but offers less favorable R:R than the golden zone.</p>
      </InfoButton></span>} subtitle="Optimal Trade Entry zones with FVG confluence">
      {ote ? (
        <div className="space-y-1">
          {/* Direction badge */}
          <div className="flex items-center gap-2 mb-3">
            <span className={`px-2 py-1 rounded text-xs font-mono font-bold ${
              ote.direction === 'LONG' ? 'bg-korinth-accent/10 text-korinth-accent' : 'bg-korinth-danger/10 text-korinth-danger'
            }`}>
              {ote.direction}
            </span>
            <span className="text-[10px] text-korinth-muted">
              London H: {ote.londonHigh?.toFixed(2)} | L: {ote.londonLow?.toFixed(2)}
            </span>
          </div>

          {/* Fib ladder */}
          {Object.entries(ote.fibLevels || {})
            .sort((a, b) => parseFloat(b[1]) - parseFloat(a[1]))
            .map(([fib, price]) => {
              const fibConfig = FIB_LABELS[fib] || { label: fib, color: 'text-korinth-muted' };
              const isGoldenZone = parseFloat(fib) >= 0.62 && parseFloat(fib) <= 0.79;

              return (
                <div
                  key={fib}
                  className={`flex items-center justify-between px-3 py-2 rounded ${
                    isGoldenZone ? 'bg-korinth-accent/10 border border-korinth-accent/20' : 'bg-korinth-bg'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${isGoldenZone ? 'bg-korinth-accent' : 'bg-korinth-border'}`} />
                    <span className={`text-xs font-mono ${fibConfig.color}`}>{fibConfig.label}</span>
                    {isGoldenZone && <span className="text-[9px] px-1 py-0.5 rounded bg-korinth-accent/20 text-korinth-accent font-mono">GOLDEN ZONE</span>}
                  </div>
                  <span className="text-sm font-mono font-bold text-white">{parseFloat(price).toFixed(2)}</span>
                </div>
              );
            })}

          {/* FVGs */}
          {fvgs.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-mono text-korinth-muted uppercase tracking-wider mb-2">Fair Value Gaps ({fvgs.length})
                <InfoButton title="Fair Value Gaps (FVG)">
                  <p>A <b>Fair Value Gap</b> is a price imbalance on the chart — a gap between candle 1's high and candle 3's low (bullish) or candle 1's low and candle 3's high (bearish).</p>
                  <p>FVGs act as magnets — price tends to return to fill them. When an FVG overlaps with the OTE golden zone, it creates a <b>confluence entry</b> (high probability setup).</p>
                  <p><b>Bullish FVG</b> — gap up, acts as support. <b>Bearish FVG</b> — gap down, acts as resistance.</p>
                </InfoButton>
              </h4>
              <div className="space-y-1">
                {fvgs.slice(-5).map((fvg, i) => (
                  <div key={i} className={`flex items-center justify-between px-3 py-1.5 rounded text-xs font-mono ${
                    fvg.type === 'BULLISH' ? 'bg-green-500/5 text-green-400' : 'bg-red-500/5 text-red-400'
                  }`}>
                    <span>{fvg.type}</span>
                    <span>{parseFloat(fvg.bottom).toFixed(2)} — {parseFloat(fvg.top).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-6">
          {data?.session?.locked ? (
            <p className="text-xs text-korinth-warning font-mono">
              No OTE levels — {data.session.ny_profile === 'CONSOLIDATION' ? 'Consolidation profile (no clear direction)' : data.session.ny_profile === 'SEEK_AND_DESTROY' ? 'Seek & Destroy profile (both sides swept)' : 'waiting for profile classification'}
            </p>
          ) : (
            <p className="text-xs text-korinth-muted font-mono">
              OTE levels available after London session locks (12:30 UTC / 16:30 Dubai)
            </p>
          )}
        </div>
      )}
    </ModuleContainer>
  );
}
